import React, { useEffect, useState } from 'react';
import DashboardHeader from '../components/Other/DashboardHeader';
import PageHeader from '../components/PageHeader';
import { getBlockUser, blockUnblockUser } from '../services/userService';
import PageContainer from '../components/pages/PageContainer';
import { toast } from 'react-toastify'; // Added toast for notifications
import 'react-toastify/dist/ReactToastify.css'; // Import toast styles

const UserBlockList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState({});

  const fallbackImage = process.env.REACT_APP_FALLBACK_IMAGE_URL || '/default-user.png'; // Added fallback default

  const fetchBlockedUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getBlockUser();
      setUsers(Array.isArray(data) ? data : []); // Ensure data is array
    } catch (error) {
      setError('Failed to load blocked users. Please try again.');
      // Error toast is handled by centralized error handler
    } finally {
      setLoading(false);
    }
  };

  const handleUnblockUser = async (userId) => {
    try {
      setProcessing(prev => ({ ...prev, [userId]: true }));
      const response = await blockUnblockUser(userId);
      if (response.success) {
        setUsers(users.filter(user => user.id !== userId));
        toast.success('User unblocked successfully');
      } else {
        setError(response.message || 'Failed to unblock user');
        toast.error(response.message || 'Failed to unblock user');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'An error occurred while unblocking user';
      setError(errorMessage);
      // Error toast is handled by centralized error handler
    } finally {
      setProcessing(prev => ({ ...prev, [userId]: false }));
    }
  };

  useEffect(() => {
    fetchBlockedUsers();
  }, []);

  return (
    <>
      <DashboardHeader />
      <PageContainer customClass="max-w-4xl mx-auto w-full">
        <PageHeader title="Blocked Users" />
        
        {/* Error Message */}
        {error && (
          <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4" role="alert">
            <p>{error}</p>
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
            <p className="text-white mt-2">Loading blocked users...</p>
          </div>
        ) : (
          /* User List */
          <div className="space-y-px">
            {users.length === 0 ? (
              <div className="text-center py-8 bg-gray-800 rounded-lg">
                <p className="text-white text-lg">No blocked users found</p>
                <p className="text-gray-400 mt-2">You haven't blocked any users yet.</p>
              </div>
            ) : (
              users.map((user) => (
                <div key={user.id} className="border-b border-white p-4">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center">
                      <img
                        src={user.user?.image || fallbackImage}
                        alt={user.user?.full_name || 'User'}
                        className="w-10 h-10 rounded-full mr-4 object-cover"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = fallbackImage;
                        }}
                      />
                      <span className="text-lg font-medium text-white">
                        {user.user?.full_name || 'Unknown User'}
                      </span>
                    </div>
                    <button
                      className="bg-white text-red-500 px-4 py-1 rounded-[14px] hover:bg-gray-100 transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={() => handleUnblockUser(user.id)}
                      disabled={processing[user.id]}
                    >
                      {processing[user.id] ? (
                        <span className="flex items-center">
                          <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          Unblocking...
                        </span>
                      ) : (
                        'Unblock'
                      )}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </PageContainer>
    </>
  );
};

export default UserBlockList;